/**
 * GrtSupplyBreakdown — V2.K.4
 *
 * Renders a supply breakdown for GRT on the TheGraphMonitor page.
 *
 * Why GRT is its own story :
 *  - NO max supply cap. Inflation is governance-controlled (~3 %
 *    today, can dial down to 0 %), used to fund indexer rewards.
 *  - Burn flows : 1 % on query fees + 1 % on curation actions +
 *    100 % of deposit taxes. Average ~1 % annual burn at writing.
 *  - Net inflation ≈ +2 %/year — but the trajectory should bend
 *    after Horizon (Dec 2025), as each new service shipped under
 *    the modular data-layer (Token API, Tycho, Amp...) adds fee +
 *    burn streams without adding to mint.
 *
 * The breakdown surfaces :
 *   - Circulating supply
 *   - Annual mint (indexer rewards) vs annual burn (fees + taxes)
 *   - Net change visualization
 *   - Horizon upgrade context (delegation tax removed)
 *   - Official Graph blog + Academy + Tokenomist links
 */

import { useTranslation } from 'react-i18next';
import { useGrtSupply } from '../../hooks/useGrtSupply';

// Verified public references — no wallet addresses are hardcoded.
// The Graph's burn is protocol-level (deducted from fees / taxes),
// not via a 0x…dEaD-style burn address.
const KNOWN_ENDPOINTS: Array<{
  label: string;
  url: string;
  category: 'docs' | 'blog' | 'tokenomist';
  note: string;
}> = [
  {
    label: 'Horizon Upgrade Live (blog officiel)',
    url: 'https://thegraph.com/blog/horizon-upgrade-live/',
    category: 'blog',
    note: 'Annonce officielle Horizon (déc. 2025) : data-layer modulaire, suppression de la delegation tax.',
  },
  {
    label: 'Horizon Implementation Guide',
    url: 'https://thegraph.com/blog/horizon-implementation-guide/',
    category: 'blog',
    note: 'Changements concrets pour Indexers, Curators, Delegators après Horizon.',
  },
  {
    label: 'The Graph Academy — Token Economics',
    url: 'https://thegraph.academy/ecosystem/graph-token-economics/',
    category: 'docs',
    note: 'Mécanique mint / burn / staking / participants.',
  },
  {
    label: 'Tokenomist — GRT supply & unlocks',
    url: 'https://www.tokenomist.ai/the-graph/tokenomics',
    category: 'tokenomist',
    note: 'Vesting schedule, allocations historiques.',
  },
];

export function GrtSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useGrtSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtB = (n: number) =>
    `${(n / 1_000_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} Md`;

  return (
    <section
      aria-labelledby="grt-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="grt-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#6747ED' }}>◈</span>
          {t('grt.supply.title', 'Supply GRT — répartition')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'grt.supply.subtitle',
            'Pas de cap maximal — l\'inflation indexer (~3 %) est compensée par le burn de frais (~1 %). L\'upgrade Horizon (déc. 2025) doit bend cette trajectoire en ajoutant des flux de fees.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <StatGrid data={data} fmt={fmt} fmtB={fmtB} t={t} />
          <MintBurnBar data={data} t={t} />
          <Legend t={t} />
          <HorizonNote t={t} fmt={fmt} data={data} />
          <KnownEndpoints t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stat grid
// ─────────────────────────────────────────────────────────────────────

interface StatGridProps {
  data: NonNullable<ReturnType<typeof useGrtSupply>['data']>;
  fmt: (n: number) => string;
  fmtB: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function StatGrid({ data, fmt, fmtB, t }: StatGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <StatCard
        label={t('grt.supply.circulating', 'En circulation')}
        value={fmtB(data.circulating)}
        sub={`${fmt(data.circulating)} GRT`}
        color="#6747ED"
      />
      <StatCard
        label={t('grt.supply.mint', 'Mint annuel (indexer rewards)')}
        value={`~${fmtB(data.annualMint)}`}
        sub={`${data.annualInflationPct.toFixed(2)} % inflation`}
        color="#f59e0b"
      />
      <StatCard
        label={t('grt.supply.burn', 'Burn annuel (fees + taxes)')}
        value={`~${fmtB(data.annualBurn)}`}
        sub={`${data.annualBurnPct.toFixed(2)} % de la supply`}
        color="#10b981"
      />
      <StatCard
        label={t('grt.supply.net', 'Inflation nette estimée')}
        value={`${data.netAnnualPct >= 0 ? '+' : ''}${data.netAnnualPct.toFixed(2)} %`}
        sub={t('grt.supply.netNote', 'Trajectoire visée : neutre / déflationniste post-Horizon')}
        color={data.netAnnualPct > 0 ? '#ef4444' : '#10b981'}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Mint vs Burn bar (relative weights, %)
// ─────────────────────────────────────────────────────────────────────

function MintBurnBar({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useGrtSupply>['data']>;
  t: (key: string, fallback: string) => string;
}) {
  // Render the two flows side by side, scaled relative to the bigger
  // of the two (so the user grasps mint > burn at a glance).
  const maxRate = Math.max(data.annualInflationPct, data.annualBurnPct, 0.01);
  const mintPct = (data.annualInflationPct / maxRate) * 50; // each half is 50%
  const burnPct = (data.annualBurnPct / maxRate) * 50;

  return (
    <div
      style={{ marginBottom: '8px' }}
      aria-label={t(
        'grt.supply.barAlt',
        'Comparaison visuelle du mint (indexer rewards) et du burn (fees + taxes)',
      )}
      role="img"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {/* Mint side */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              width: `${mintPct * 2}%`,
              height: '24px',
              background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.4), #f59e0b)',
              borderRadius: '8px 0 0 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '8px',
              color: 'white',
              fontSize: '11px',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              transition: 'width 0.5s ease',
            }}
            title={`Mint : ${data.annualInflationPct.toFixed(2)} %`}
          >
            +{data.annualInflationPct.toFixed(1)} %
          </div>
        </div>

        {/* Axis */}
        <div
          aria-hidden
          style={{
            width: '2px',
            height: '28px',
            background: 'var(--border)',
            borderRadius: '2px',
          }}
        />

        {/* Burn side */}
        <div style={{ flex: 1, display: 'flex' }}>
          <div
            style={{
              width: `${burnPct * 2}%`,
              height: '24px',
              background: 'linear-gradient(90deg, #10b981, rgba(16, 185, 129, 0.4))',
              borderRadius: '0 8px 8px 0',
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '8px',
              color: 'white',
              fontSize: '11px',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              transition: 'width 0.5s ease',
            }}
            title={`Burn : ${data.annualBurnPct.toFixed(2)} %`}
          >
            −{data.annualBurnPct.toFixed(1)} %
          </div>
        </div>
      </div>
    </div>
  );
}

function Legend({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: 'var(--muted)',
        marginBottom: '14px',
      }}
    >
      <LegendItem color="#f59e0b" label={t('grt.supply.legendMint', 'Mint annuel — indexer rewards')} />
      <LegendItem color="#10b981" label={t('grt.supply.legendBurn', 'Burn annuel — query fees + curation + deposit taxes')} />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        aria-hidden
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Horizon callout
// ─────────────────────────────────────────────────────────────────────

function HorizonNote({
  t,
  fmt,
  data,
}: {
  t: (key: string, fallback: string) => string;
  fmt: (n: number) => string;
  data: NonNullable<ReturnType<typeof useGrtSupply>['data']>;
}) {
  const body = t(
    'grt.supply.horizonBody',
    "L'upgrade Horizon (live depuis décembre 2025) transforme The Graph d'un protocole d'indexation unique en un data-layer modulaire multi-services (Subgraph Service Q1 2026, puis Token API, Tycho, Amp…). Conséquences sur la supply : (1) la delegation tax de 0,5 % a été supprimée — Delegators plus efficients ; (2) chaque nouveau service ajoutera ses propres flux de fees et de burn sans toucher au mint, donc la trajectoire devrait converger vers neutre voire déflationniste. Au taux actuel : ~{{mint}} GRT mintés vs ~{{burn}} GRT burnés par an, soit un net de {{net}} % sur la supply.",
  )
    .replace('{{mint}}', fmt(data.annualMint))
    .replace('{{burn}}', fmt(data.annualBurn))
    .replace(
      '{{net}}',
      `${data.netAnnualPct >= 0 ? '+' : ''}${data.netAnnualPct.toFixed(2)}`,
    );

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(103, 71, 237, 0.06)',
        border: '1px dashed rgba(103, 71, 237, 0.35)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#6747ED' }}>
        {t('grt.supply.horizonTitle', 'Horizon — data-layer modulaire (live décembre 2025)')}
      </strong>
      <div style={{ marginTop: '4px' }}>{body}</div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'grt.supply.horizonSource',
          'Sources : thegraph.com/blog/horizon-upgrade-live, thegraph.academy, Messari State of The Graph Q4 2025.',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known endpoints
// ─────────────────────────────────────────────────────────────────────

function KnownEndpoints({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('grt.supply.knownEndpointsTitle', 'Sources officielles & documentation')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'grt.supply.knownEndpointsDisclaimer',
          'The Graph effectue ses burns au niveau protocole (déduction sur les fees et taxes), pas via une adresse de burn EVM standard type 0x…dEaD. Aucune adresse de wallet n\'est hardcodée — utiliser les dashboards officiels et tokenomist pour les chiffres en live.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ENDPOINTS.map((entry) => (
          <li
            key={entry.url}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <CategoryPill category={entry.category} />
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}
              >
                {entry.label} ↗
              </a>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CategoryPill({ category }: { category: 'docs' | 'blog' | 'tokenomist' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    blog: { bg: 'rgba(103, 71, 237, 0.18)', color: '#a78bfa', label: 'Blog' },
    docs: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Docs' },
    tokenomist: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'Tokenomist' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply GRT sera affichée dès que l'API répondra.
    </div>
  );
}
